import { Button, Dialog } from "@repo/ui";
import { Upload } from "lucide-react";
import { useCallback, useRef, useState } from "react";

export interface CsvCandidat {
  nomCandidat: string;
  prenomCandidat: string;
  emailCandidat: string;
}

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (candidats: CsvCandidat[]) => void;
}

export function CsvImportModal({
  isOpen,
  onClose,
  onImport,
}: CsvImportModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<CsvCandidat[]>([]);

  const parseCsv = useCallback((text: string): CsvCandidat[] => {
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      throw new Error("Le fichier est vide");
    }

    const header = lines[0].split(",").map((h) => h.trim().toLowerCase());

    const nomIdx = header.findIndex((h) =>
      ["nom", "lastname", "nomcandidat"].includes(h),
    );
    const prenomIdx = header.findIndex((h) =>
      ["prenom", "firstname", "prenomcandidat"].includes(h),
    );
    const emailIdx = header.findIndex((h) =>
      ["email", "mail", "emailcandidat"].includes(h),
    );

    if (nomIdx === -1 || prenomIdx === -1 || emailIdx === -1) {
      throw new Error(
        `Colonnes requises: nom, prenom, email. Trouvé: ${header.join(", ")}`,
      );
    }

    const candidats: CsvCandidat[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(",").map((c) => c.trim());
      const nom = cols[nomIdx] || "";
      const prenom = cols[prenomIdx] || "";
      const email = cols[emailIdx] || "";

      if (!nom || !prenom || !email) continue;

      candidats.push({ nomCandidat: nom, prenomCandidat: prenom, emailCandidat: email });
    }

    if (candidats.length === 0) {
      throw new Error("Aucun candidat valide trouvé dans le fichier");
    }

    return candidats;
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      setPreview([]);
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target?.result as string;
          const candidats = parseCsv(text);
          setPreview(candidats);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : "Erreur de parsing CSV",
          );
        }
      };
      reader.readAsText(file);
    },
    [parseCsv],
  );

  const handleConfirm = () => {
    onImport(preview);
    setPreview([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const handleCancel = () => {
    setPreview([]);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Importer des candidats (CSV)"
      className="max-w-xl"
    >
      <p className="text-sm text-muted-foreground mb-4">
        Format attendu : colonnes nom, prenom, email (séparées par des virgules)
      </p>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
            id="csv-upload"
          />
          <label htmlFor="csv-upload">
            <Button variant="outline" asChild>
              <span>
                <Upload className="h-4 w-4 mr-2" />
                Choisir un fichier CSV
              </span>
            </Button>
          </label>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {preview.length > 0 && (
          <>
            <p className="text-sm text-muted-foreground">
              {preview.length} candidat(s) trouvé(s) :
            </p>
            <div className="max-h-48 overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-2">Prénom</th>
                    <th className="text-left p-2">Nom</th>
                    <th className="text-left p-2">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((c, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="p-2">{c.prenomCandidat}</td>
                      <td className="p-2">{c.nomCandidat}</td>
                      <td className="p-2">{c.emailCandidat}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Annuler
              </Button>
              <Button onClick={handleConfirm}>
                Ajouter {preview.length} candidat(s)
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
